import { useEffect, useRef } from "react";

/**
 * Scroll reveal using IntersectionObserver.
 * Safe-by-default: if IO is unavailable or doesn't fire (e.g. above-the-fold elements
 * already visible, headless test runners, prefers-reduced-motion), we trigger the
 * `in` state after a short grace period so content never stays hidden and parents
 * never block pointer events.
 */
export default function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("in");
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => el.classList.add("in"), delay);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(el);

    // Safety net — guarantee visible after 1.8s even if IO never fires.
    const safety = setTimeout(() => el.classList.add("in"), 1800);

    return () => {
      obs.disconnect();
      clearTimeout(safety);
    };
  }, [delay]);

  return (
    <div ref={ref} className={`reveal-clip ${className}`}>
      {children}
    </div>
  );
}
